import { WiseFormulaInfo } from './types'
import { normalizeCellAddress, extractColumnFromAddress } from './formula-utils'

// Registry to track which formulas depend on which cells
const parameterCellRegistry: Map<string, WiseFormulaInfo[]> = new Map()

// Create a unique key for the registry
export const createRegistryKey = (
    worksheetId: string,
    cellAddress: string
): string => {
    return `${worksheetId}:${normalizeCellAddress(cellAddress)}`
}

// Add a parameter cell to the registry
export const addParameterCellToRegistry = (
    worksheetId: string,
    parameterCellAddress: string,
    wiseFormulaInfo: WiseFormulaInfo
) => {
    const registryKey = createRegistryKey(worksheetId, parameterCellAddress)

    if (!parameterCellRegistry.has(registryKey)) {
        parameterCellRegistry.set(registryKey, [])
    }

    const existingFormulas = parameterCellRegistry.get(registryKey)!

    const existingIndex = existingFormulas.findIndex(
        (f) =>
            f.worksheetId === wiseFormulaInfo.worksheetId &&
            f.formulaAddress === wiseFormulaInfo.formulaAddress
    )

    if (existingIndex === -1) {
        existingFormulas.push(wiseFormulaInfo)
    } else {
        existingFormulas[existingIndex] = wiseFormulaInfo
    }
}

// Remove a formula from the registry
export const removeFormulaFromRegistry = (
    worksheetId: string,
    formulaAddress: string
) => {
    const keysToDelete: string[] = []
    parameterCellRegistry.forEach((formulas, key) => {
        const filteredFormulas = formulas.filter(
            (f: WiseFormulaInfo) =>
                !(
                    f.worksheetId === worksheetId &&
                    f.formulaAddress === formulaAddress
                )
        )

        if (filteredFormulas.length === 0) {
            keysToDelete.push(key)
        } else {
            parameterCellRegistry.set(key, filteredFormulas)
        }
    })

    keysToDelete.forEach((key) => parameterCellRegistry.delete(key))
}

// Clear all formulas for a worksheet from the registry
export const clearRegistryForWorksheet = (worksheetId: string) => {
    const keysToDelete: string[] = []
    parameterCellRegistry.forEach((formulas, key) => {
        const filteredFormulas = formulas.filter(
            (f: WiseFormulaInfo) => f.worksheetId !== worksheetId
        )

        if (filteredFormulas.length === 0) {
            keysToDelete.push(key)
        } else {
            parameterCellRegistry.set(key, filteredFormulas)
        }
    })

    keysToDelete.forEach((key) => parameterCellRegistry.delete(key))
}

// Get all formulas that depend on a specific cell
export const getDependentWiseFormulas = (
    worksheetId: string,
    cellAddress: string
): WiseFormulaInfo[] => {
    const registryKey = createRegistryKey(worksheetId, cellAddress)
    return parameterCellRegistry.get(registryKey) || []
}

// Get all formulas in a specific column for a worksheet
export const getFormulasByColumn = (
    worksheetId: string,
    column: string
): WiseFormulaInfo[] => {
    const formulas: WiseFormulaInfo[] = []
    const allFormulas = Array.from(parameterCellRegistry.values())

    for (const formulaList of allFormulas) {
        for (const formula of formulaList) {
            if (formula.worksheetId === worksheetId) {
                const formulaColumn =
                    formula.column ||
                    extractColumnFromAddress(formula.formulaAddress)
                if (formulaColumn === column) {
                    formulas.push(formula)
                }
            }
        }
    }

    return formulas
}

// Check if a formula is registered for smart detection
// Optionally accepts a column parameter for fallback search
export const isFormulaRegistered = (
    worksheetId: string,
    formulaAddress: string,
    column?: string
): boolean => {
    const normalizedAddress = normalizeCellAddress(formulaAddress)

    const allFormulas = Array.from(parameterCellRegistry.values())
    for (const formulas of allFormulas) {
        for (const formula of formulas) {
            if (
                formula.worksheetId === worksheetId &&
                normalizeCellAddress(formula.formulaAddress) ===
                    normalizedAddress
            ) {
                return true
            }
        }
    }

    // If not found by exact address and column is provided, try column-based search
    // Note: This is a fallback check - the caller should handle location updates
    if (column) {
        const formulasInColumn = getFormulasByColumn(worksheetId, column)
        // If there are formulas in this column, it might be a moved formula
        return formulasInColumn.length > 0
    }

    return false
}

// Update formula location when it moves
export const updateFormulaLocation = (
    worksheetId: string,
    oldAddress: string,
    newAddress: string
): void => {
    const normalizedOldAddress = normalizeCellAddress(oldAddress)
    const normalizedNewAddress = normalizeCellAddress(newAddress)

    // Find the formula in the registry
    let foundFormula: WiseFormulaInfo | null = null
    let foundKey: string | null = null

    parameterCellRegistry.forEach((formulas, key) => {
        const index = formulas.findIndex(
            (f) =>
                f.worksheetId === worksheetId &&
                normalizeCellAddress(f.formulaAddress) === normalizedOldAddress
        )

        if (index !== -1) {
            foundFormula = { ...formulas[index] }
            foundKey = key
        }
    })

    if (foundFormula && foundKey) {
        // Update the formula address
        foundFormula.formulaAddress = normalizedNewAddress

        // Update column if not already set
        if (!foundFormula.column) {
            foundFormula.column = extractColumnFromAddress(newAddress)
        }

        // Update the formula in all registry entries
        parameterCellRegistry.forEach((formulas, key) => {
            const index = formulas.findIndex(
                (f) =>
                    f.worksheetId === worksheetId &&
                    normalizeCellAddress(f.formulaAddress) ===
                        normalizedOldAddress
            )

            if (index !== -1) {
                formulas[index] = foundFormula!
            }
        })
    }
}

// Update the data range for a formula (called after data is written)
export const updateFormulaDataRange = (
    worksheetId: string,
    formulaAddress: string,
    dataRange: string
): void => {
    const normalizedAddress = normalizeCellAddress(formulaAddress)

    // Find and update the formula in all registry entries
    parameterCellRegistry.forEach((formulas) => {
        const index = formulas.findIndex(
            (f) =>
                f.worksheetId === worksheetId &&
                normalizeCellAddress(f.formulaAddress) === normalizedAddress
        )

        if (index !== -1) {
            formulas[index].dataRange = dataRange
        }
    })
}

// Get formula info by address (for retrieving stored data range)
export const getFormulaInfo = (
    worksheetId: string,
    formulaAddress: string
): WiseFormulaInfo | null => {
    const normalizedAddress = normalizeCellAddress(formulaAddress)

    const allFormulas = Array.from(parameterCellRegistry.values())
    for (const formulas of allFormulas) {
        for (const formula of formulas) {
            if (
                formula.worksheetId === worksheetId &&
                normalizeCellAddress(formula.formulaAddress) ===
                    normalizedAddress
            ) {
                return formula
            }
        }
    }

    return null
}
